import React, { useState, useEffect } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { userLedger } from '../../api/endpoints';
import {
    FaFilter,
    FaSort,
    FaSortUp,
    FaSortDown,
    FaCalendarAlt,
} from "react-icons/fa";
import {
    FiAlertTriangle, FiAlertCircle, FiArrowLeft, FiBookOpen, FiArrowDown,
    FiArrowUp, FiGift, FiAward, FiActivity, FiInbox, FiChevronLeft,
    FiChevronRight
} from "react-icons/fi";

const Ledger = () => {
    const { userId } = useParams();
    const location = useLocation();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [ledgerData, setLedgerData] = useState({
        ledger: [],
        pagination: {
            total: 0,
            page: 1,
            limit: 10,
            totalPages: 0
        }
    });

    // Filter states
    const [currentPage, setCurrentPage] = useState(1);
    const [entriesPerPage, setEntriesPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [sortBy, setSortBy] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState('desc');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [showFilter, setShowFilter] = useState(false);

    // Fetch ledger data with all filters
    useEffect(() => {
        const fetchLedgerData = async () => {
            if (!userId) {
                setError('No user ID provided. Please select a user to view their ledger.');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);

                const params = {
                    page: currentPage,
                    limit: entriesPerPage,
                    sortBy: sortBy,
                    sortOrder: sortOrder,
                };

                // Add filters only if they have values
                if (searchTerm) params.search = searchTerm;
                if (filterType) params.type = filterType;
                if (filterStatus) params.status = filterStatus;
                if (startDate) params.startDate = startDate;
                if (endDate) params.endDate = endDate;

                console.log('Fetching ledger with params:', params);

                const response = await userLedger(userId, params);

                console.log('API Response:', response);

                if (response.success) {
                    setLedgerData(response.data);
                } else {
                    setError(response.message || 'Failed to fetch ledger data');
                }
            } catch (err) {
                console.error('Fetch ledger error:', err);
                setError(err.response?.data?.message || err.message || 'An error occurred while fetching ledger data');
            } finally {
                setLoading(false);
            }
        };

        fetchLedgerData();
    }, [userId, currentPage, entriesPerPage, searchTerm, filterType, filterStatus, sortBy, sortOrder, startDate, endDate]);

    // Handle sort change
    const handleSort = (field) => {
        if (sortBy === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortOrder('desc');
        }
        setCurrentPage(1);
    };

    // Handle page change
    const handlePageChange = (pageNumber) => {
        if (pageNumber >= 1 && pageNumber <= ledgerData.pagination.totalPages) {
            setCurrentPage(pageNumber);
        }
    };

    // Handle entries per page change
    const handleEntriesPerPageChange = (value) => {
        setEntriesPerPage(Number(value));
        setCurrentPage(1);
    };

    // Handle filter apply
    const applyFilters = () => {
        setCurrentPage(1);
    };

    // Reset filters
    const resetFilters = () => {
        setSearchTerm('');
        setFilterType('');
        setFilterStatus('');
        setStartDate('');
        setEndDate('');
        setSortBy('createdAt');
        setSortOrder('desc');
        setCurrentPage(1);
    };

    // Get status badge class
    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'SUCCESS':
                return 'bg-success';
            case 'PENDING':
                return 'bg-warning text-dark';
            case 'FAILED':
                return 'bg-danger';
            case 'CANCELLED':
                return 'bg-secondary';
            default:
                return 'bg-info';
        }
    };

    // Get type badge class
    const getTypeBadgeClass = (type) => {
        switch (type) {
            case 'DEPOSIT':
                return 'badge-deposit';
            case 'WITHDRAW':
                return 'badge-withdraw';
            case 'BONUS':
                return 'badge-bonus';
            case 'PENALTY':
                return 'badge-penalty';
            case 'WIN':
                return 'badge-win';
            case 'BET':
                return 'badge-bet';
            default:
                return 'badge-default';
        }
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    // Get sort icon
    const getSortIcon = (field) => {
        if (sortBy !== field) return <FaSort className="text-muted" />;
        return sortOrder === 'asc' ? <FaSortUp className="text-primary" /> : <FaSortDown className="text-primary" />;
    };

    // Show error if no userId
    if (!userId) {
        return (
            <div className="lead-page">
                <div className="card shadow">
                    <div className="card-body">
                        <div className="alert alert-warning">
                            <FiAlertTriangle className="me-2" />
                            <span>No user ID provided. Please select a user to view their ledger.</span>
                        </div>
                        <div className="text-center mt-3">
                            <Link to="/" className="btn btn-primary">
                                <FiArrowLeft className="me-2" />
                                Go Back to User List
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="lead-page">
                <div className="card shadow">
                    <div className="card-body text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3">Loading ledger data...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="lead-page">
                <div className="card shadow">
                    <div className="card-body">
                        <div className="alert alert-danger">
                            <FiAlertCircle className="me-2" />
                            <span>{error}</span>
                        </div>
                        <div className="text-center mt-3">
                            <Link to="/" className="btn btn-primary">
                                <FiArrowLeft className="me-2" />
                                Go Back to User List
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="lead-page">
            <div className="card shadow">
                <div className="card-header d-flex justify-content-between align-items-center flex-wrap">
                    <div>
                        <h5 className="card-title mb-0">
                            <FiBookOpen className="me-2" />
                            Ledger
                        </h5>
                        <div className="mt-1">
                            <span className="badge bg-info me-2">
                                User: {userId}
                            </span>
                            <span className="badge bg-secondary">
                                Total: {ledgerData.pagination?.total || 0} transactions
                            </span>
                        </div>
                    </div>

                    <div className="d-flex gap-2 align-items-center flex-wrap mt-2 mt-sm-0">
                        <button
                            className="btn btn-dark btn-sm"
                            onClick={() => setShowFilter(!showFilter)}
                        >
                            <FaFilter />
                            Filter
                        </button>
                    </div>
                </div>

                <div className="card-body">
                    {/* Filters */}
                    {showFilter && (
                        <div className="filters-section mb-3 p-3 bg-light rounded">
                            <div className="row g-3">
                                <div className="col-md-3">
                                    <label className="form-label">Search</label>
                                    <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        placeholder="Search by ID, Remark..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label">Type</label>
                                    <select
                                        className="form-select form-select-sm"
                                        value={filterType}
                                        onChange={(e) => setFilterType(e.target.value)}
                                    >
                                        <option value="">All Types</option>
                                        <option value="DEPOSIT">Deposit</option>
                                        <option value="WITHDRAW">Withdraw</option>
                                        <option value="BONUS">Bonus</option>
                                        <option value="PENALTY">Penalty</option>
                                        <option value="WIN">Win</option>
                                        <option value="BET">Bet</option>
                                    </select>
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label">Status</label>
                                    <select
                                        className="form-select form-select-sm"
                                        value={filterStatus}
                                        onChange={(e) => setFilterStatus(e.target.value)}
                                    >
                                        <option value="">All Status</option>
                                        <option value="SUCCESS">Success</option>
                                        <option value="PENDING">Pending</option>
                                        <option value="FAILED">Failed</option>
                                        <option value="CANCELLED">Cancelled</option>
                                    </select>
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label">From Date</label>
                                    <input
                                        type="date"
                                        className="form-control form-control-sm"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                    />
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label">To Date</label>
                                    <input
                                        type="date"
                                        className="form-control form-control-sm"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                    />
                                </div>
                                <div className="col-md-1 d-flex align-items-end">
                                    <button
                                        className="btn btn-danger btn-sm w-100"
                                        onClick={resetFilters}
                                    >
                                        Reset
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Entries Per Page */}
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <div className="d-flex align-items-center gap-2">
                            <label className="mb-0">Show:</label>
                            <select
                                className="form-select form-select-sm"
                                style={{ width: '70px' }}
                                value={entriesPerPage}
                                onChange={(e) => handleEntriesPerPageChange(e.target.value)}
                            >
                                <option value="10">10</option>
                                <option value="25">25</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                            </select>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="table-responsive">
                        <table className="table table-bordered table-hover table-striped align-middle">
                            <thead className="table-dark">
                                <tr>
                                    <th style={{ width: '50px' }}>#</th>
                                    <th onClick={() => handleSort('transactionId')} style={{ cursor: 'pointer' }}>
                                        Transaction ID {getSortIcon('transactionId')}
                                    </th>
                                    {/* <th>User ID</th> */}
                                    <th onClick={() => handleSort('type')} style={{ cursor: 'pointer', width: '100px' }}>
                                        Type {getSortIcon('type')}
                                    </th>
                                    <th onClick={() => handleSort('amount')} style={{ cursor: 'pointer', width: '120px' }}>
                                        Amount {getSortIcon('amount')}
                                    </th>
                                    <th onClick={() => handleSort('closingBalance')} style={{ cursor: 'pointer', width: '120px' }}>
                                        Closing Balance {getSortIcon('closingBalance')}
                                    </th>
                                    <th onClick={() => handleSort('status')} style={{ cursor: 'pointer', width: '100px' }}>
                                        Status {getSortIcon('status')}
                                    </th>
                                    <th>Remark</th>
                                    <th onClick={() => handleSort('createdAt')} style={{ cursor: 'pointer', width: '180px' }}>
                                        Created At {getSortIcon('createdAt')}
                                    </th>
                                </tr>
                            </thead>

                            <tbody>
                                {ledgerData.ledger && ledgerData.ledger.length > 0 ? (
                                    ledgerData.ledger.map((item, index) => (
                                        <tr key={item._id}>
                                            <td>{(currentPage - 1) * entriesPerPage + index + 1}</td>
                                            <td className="transaction-id" style={{ fontSize: '12px', fontFamily: 'monospace' }}>
                                                {item.transactionId}
                                            </td>
                                            {/* <td className="user-id" style={{ fontSize: '12px', fontFamily: 'monospace' }}>
                                                {item.userId}
                                            </td> */}
                                            <td>
                                                <span className={`type-badge ${getTypeBadgeClass(item.type)}`}>
                                                    {item.type === 'DEPOSIT' ? <FiArrowDown /> :
                                                        item.type === 'WITHDRAW' ? <FiArrowUp /> :
                                                            item.type === 'BONUS' ? <FiGift /> :
                                                                item.type === 'WIN' ? <FiAward /> :
                                                                    item.type === 'BET' ? <FiActivity /> :
                                                                        <FiAlertTriangle />}
                                                    {item.type}
                                                </span>
                                            </td>
                                            <td className={`amount ${item.type === 'DEPOSIT' || item.type === 'BONUS' || item.type === 'WIN' ? 'positive' : 'negative'}`}>
                                                {item.type === 'DEPOSIT' || item.type === 'BONUS' || item.type === 'WIN' ? '+' : '-'}
                                                ₹{item.amount?.toLocaleString() || 0}
                                            </td>
                                            <td>₹{item.closingBalance?.toLocaleString() || 0}</td>
                                            <td>
                                                <span className={`badge ${getStatusBadgeClass(item.status)}`}>
                                                    {item.status}
                                                </span>
                                            </td>
                                            <td className="remark-cell" style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {item.remark || 'N/A'}
                                            </td>
                                            <td className="date-cell" style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                                                {formatDate(item.createdAt)}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="9" className="text-center py-4">
                                            <div className="empty-state-content">
                                                <FiInbox size={34} className="text-muted" />
                                                <p className="mt-2">No transactions found</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {ledgerData.pagination && ledgerData.pagination.total > 0 && (
                        <div className="d-flex justify-content-between align-items-center flex-wrap mt-3">
                            <div className="showing-info">
                                Showing {(currentPage - 1) * entriesPerPage + 1} to {Math.min(currentPage * entriesPerPage, ledgerData.pagination.total)} of {ledgerData.pagination.total} entries
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <button
                                    className="btn btn-sm btn-secondary"
                                    disabled={currentPage === 1}
                                    onClick={() => handlePageChange(currentPage - 1)}
                                >
                                    <FiChevronLeft /> Previous
                                </button>
                                <span className="mx-2">
                                    Page {currentPage} of {ledgerData.pagination.totalPages || 1}
                                </span>
                                <button
                                    className="btn btn-sm btn-secondary"
                                    disabled={currentPage === ledgerData.pagination.totalPages || ledgerData.pagination.totalPages === 0}
                                    onClick={() => handlePageChange(currentPage + 1)}
                                >
                                    Next <FiChevronRight />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Ledger;